/**
 * Quantity model for the school order sheet's component editor.
 *
 * The school orders MEAL counts; each component of a decomposable bundle
 * then ships a quantity derived from the campus's delivery rate:
 *
 *     qty = ceil(mealQty × rateBps / 10000)      // ALWAYS rounds up
 *
 * computed DIRECTLY from basis points. The older model stored an integer
 * PERCENT and derived the quantity from that, which is a lossy encoding of
 * "how many": a 6750 bps (67.5%) rate became `round(67.5)` = 68%, and 200
 * meals then derived 136 — while `deriveMilkLines` and the server's own
 * `DeliveryRateResolver.expandShape` both computed 135 from the same bps.
 * Removing the percent hop makes all three agree by construction.
 *
 * Two separate ideas, deliberately not conflated:
 *
 *   TYPED    — the school entered this number. Sparse: only kinds actually
 *              typed appear in the map, and an explicit `0` is a real value
 *              ("ship none of this"), distinct from "not typed".
 *   DERIVED  — everything else, a pure function of (mealQty, rateBps).
 *
 * So "is this cell customized?" is `kind in typed`, while "does this line
 * ship something other than one-per-meal?" is a question about the RESOLVED
 * quantities. The percentage model had a single `isDefault` doing both, and
 * got both slightly wrong.
 *
 * Pure (no React) — unit-tested in `client/test/lib/school-orders/`.
 */

import { BUNDLE_TITLE_KIND_ORDER } from '@ordo/constants/bundle-title'

import type { ComponentKind } from '@/api/location-settings'
import { HIDDEN_SPLIT_KINDS } from '@/api/location-settings'

import type { BundlePartWire } from './breakdown'
import { componentKindDisplayLabel, splitKinds } from './breakdown'

/** A rate of 10000 bps = 100% — one unit per meal ordered. */
export const FULL_RATE_BPS = 10000

/**
 * The quantity a component ships for `mealQty` meals at `rateBps`.
 *
 * ROUNDS UP (Sheila 2026-07-30) — over-prep beats under-prep. Identical
 * expression to `deriveMilkLines` and to the server's `expandShape`, so the
 * sheet, the derived-milk block and the server default can never disagree.
 * A missing rate row means 100%, matching `LocationComponentDeliveryRate`'s
 * "missing row = 100%" rule.
 *
 * MAIN_ENTREE is not special here — callers pass `FULL_RATE_BPS` for it (a
 * meal always ships one main), which `defaultComponentQuantities` does.
 */
export function componentQtyFromRate(mealQty: number, rateBps: number | undefined): number {
  if (mealQty <= 0) {
    return 0
  }
  const bps = Math.min(FULL_RATE_BPS, Math.max(0, rateBps ?? FULL_RATE_BPS))
  return Math.max(0, Math.ceil((mealQty * bps) / FULL_RATE_BPS))
}

/**
 * The school's default quantity for every split-visible kind of a bundle —
 * what ships when the school types nothing. MAIN_ENTREE is pinned to the
 * meal count (one main per meal ordered; `expandShape` forces the same).
 */
export function defaultComponentQuantities(
  rates: Partial<Record<ComponentKind, number>>,
  parts: readonly BundlePartWire[],
  mealQty: number,
): Record<string, number> {
  const partCounts = kindPartCounts(parts)
  const quantities: Record<string, number> = {}
  for (const kind of splitKinds(parts)) {
    // Round ONCE at the kind level: a kind in N parts serves N per meal,
    // so its default is ceil(N × meals × rate) — per-part ceils compounded
    // (two 50% wedge cups on 25 meals derived 13+13=26; the total must be
    // 25, split across the cups at fulfillment). Matches the server's
    // expandShape exactly.
    quantities[kind] =
      kind === 'MAIN_ENTREE'
        ? Math.max(0, mealQty)
        : componentQtyFromRate(mealQty * (partCounts[kind] ?? 1), rates[kind])
  }
  return quantities
}

/** How many bundle parts carry each kind — the per-meal multiplier for caps + defaults. */
export function kindPartCounts(parts: readonly BundlePartWire[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const part of parts) {
    counts[part.componentKind] = (counts[part.componentKind] ?? 0) + 1
  }
  return counts
}

/**
 * What actually ships: the school's typed numbers where present, the
 * derived default everywhere else. MAIN_ENTREE is always the meal count —
 * it is never typeable, and the server re-forces it regardless.
 *
 * Typed values are CAPPED at the bundle portion count HERE, at resolve time — never
 * written back capped into the typed buffer. The buffer must stay raw:
 * the quantity input commits on every keystroke, so typing "180" passes
 * through meal counts 1 and 18, and a buffer capped at those transients
 * ratchets {FRUIT: 40} down to 1 with no way back (the Hall MS week-of-8/24
 * incident). Capping at read keeps displayed == shipped for any meal count
 * while the underlying numbers survive the keystrokes.
 *
 * `rateBps` carries the rate basis captured when the line was built, so
 * this resolves without access to the settings query. Its KEY SET also
 * carries which kinds the bundle has, which is what lets the cart store
 * and the checkout page re-resolve without the bundle's parts.
 */
export function resolveComponentQuantities(args: {
  mealQty: number
  typed?: Record<string, number> | null
  rateBps?: Record<string, number> | null
  partCounts?: Record<string, number> | null
}): Record<string, number> {
  const { mealQty, typed, rateBps, partCounts } = args
  const kinds = new Set<string>([...Object.keys(rateBps ?? {}), ...Object.keys(typed ?? {})])
  const resolved: Record<string, number> = {}
  for (const kind of kinds) {
    if (kind === 'MAIN_ENTREE') {
      resolved[kind] = Math.max(0, mealQty)
      continue
    }
    const own = typed?.[kind]
    const portionQty = mealQty * Math.max(1, partCounts?.[kind] ?? 1)
    resolved[kind] =
      own !== undefined
        ? Math.min(Math.max(0, own), Math.max(0, portionQty))
        : componentQtyFromRate(portionQty, rateBps?.[kind])
  }
  return resolved
}

/**
 * Cap a typed component quantity at the meal count (Sheila 2026-07-30 —
 * a component can never exceed meals ordered). Preserves the invariant the
 * percentage model got for free from its 0-100 clamp. MAIN_ENTREE is the
 * meal count by definition.
 */
export function capComponentQty(qty: number, mealQty: number, partCount = 1): number {
  if (!Number.isFinite(qty) || qty <= 0) {
    return 0
  }
  // A kind in N bundle parts serves up to N per meal; callers without the
  // bundle in scope pass no count and keep the original meals ceiling.
  return Math.min(Math.floor(qty), Math.max(0, mealQty * Math.max(1, partCount)))
}

/**
 * Re-cap every typed quantity after the meal count changes — a school that
 * typed 135 fruit against 200 meals and then drops to 120 meals must not
 * keep shipping 135. Returns a new map; kinds that were not typed stay
 * untyped (they re-derive on their own).
 */
export function capTypedQuantities(
  typed: Record<string, number> | null | undefined,
  mealQty: number,
  partCounts?: Record<string, number>,
): Record<string, number> | null {
  if (!typed || Object.keys(typed).length === 0) {
    return null
  }
  const next: Record<string, number> = {}
  for (const [kind, qty] of Object.entries(typed)) {
    next[kind] = capComponentQty(qty, mealQty, partCounts?.[kind] ?? 1)
  }
  return next
}

/**
 * Read a PLACED order's per-kind quantities back into the editor as TYPED
 * values. `componentBreakdown` is already quantities, so this is lossless —
 * the percentage model round-tripped through an integer percent and could
 * display 136 for an order of 135.
 *
 * A split-visible kind of the bundle that is ABSENT from a non-null
 * breakdown resolves to 0, not to the default: an explicit zero produces no
 * child row server-side, so the kind simply doesn't come back. Treating it
 * as "unset" would silently re-inflate it to the default on the next save.
 *
 * `typedKinds` is the order's PROVENANCE (`componentQuantitiesTypedKinds`):
 *
 *   null    LEGACY order, placed before provenance existed. Every kind comes
 *           back as typed — the COMPLETE map, today's behaviour — so nothing
 *           re-derives behind the school's back. Legacy orders recover
 *           through the explicit "Reset components" action.
 *   []      Nothing was typed. Returns null: the editor holds no typed
 *           values and every cell derives from the CURRENT rates.
 *   [...]   Only those kinds come back, SPARSE, so their untyped siblings
 *           derive from the current rates.
 *
 * The sparseness is the whole point. Before provenance this returned a
 * complete map unconditionally, which marked every kind as typed and meant a
 * placed day could never pick up a rate change — a district that raised
 * BEVERAGE from 0% to 20% had to strip milk from placed orders by hand.
 */
export function componentQuantitiesFromBreakdown(
  breakdown: Record<string, number> | null | undefined,
  parts: readonly BundlePartWire[],
  mealQty: number,
  typedKinds?: readonly string[] | null,
): Record<string, number> | null {
  if (!breakdown || mealQty <= 0) {
    return null
  }
  const typed = typedKinds == null ? null : new Set(typedKinds)
  const quantities: Record<string, number> = {}
  for (const kind of splitKinds(parts)) {
    // MAIN_ENTREE is the meal count by definition and is never re-derived,
    // so it stays pinned regardless of provenance.
    if (kind === 'MAIN_ENTREE') {
      quantities[kind] = Math.max(0, mealQty)
      continue
    }
    if (typed !== null && !typed.has(kind)) {
      continue // untyped — leave it out so it derives from current rates
    }
    quantities[kind] = Math.max(0, breakdown[kind] ?? 0)
  }
  // Only MAIN_ENTREE survived: there is nothing typed to seed, so report "no
  // typed values" and let every cell derive.
  return Object.keys(quantities).some((kind) => kind !== 'MAIN_ENTREE') ? quantities : null
}

/**
 * True when the line ships something other than one-of-everything-per-meal
 * — drives the "custom" state on the collapsed row. Hidden kinds (SIDE,
 * SUPPLEMENT) are ignored exactly as before: they have no editor row, so a
 * deviation on them (e.g. from an old placed order) must not read as custom.
 */
export function isCustomComponentQuantities(
  resolved: Record<string, number> | null | undefined,
  mealQty: number,
): boolean {
  if (!resolved) {
    return false
  }
  return Object.entries(resolved).some(
    ([kind, qty]) => kind !== 'MAIN_ENTREE' && !HIDDEN_SPLIT_KINDS.has(kind) && qty !== mealQty,
  )
}

/**
 * Whether a cart line should CARRY its rate basis.
 *
 * The basis's key set is the bundle's shape, and `overridesFromQuantities`
 * derives the wire map's key set from (basis ∪ typed). The shipment
 * materializer reads a kind MISSING from a non-null `kindCounts` as **0**
 * (`shipment-materializer.ts:1155`), so an INCOMPLETE map silently drops
 * components.
 *
 * Hence the rule: withhold the basis only when the line has no component
 * opinion at all — pre-fill off and nothing typed — which is what preserves
 * the server's legacy uniform expansion for campuses that never opted in.
 * The moment ANY kind is typed the line must carry the complete basis.
 *
 * Regression this pins (PR #1580 review): `prefillComponentSplitEnabled` is
 * `@default(false)`, so on the DEFAULT campus typing one component used to
 * ship `{FRUIT: 50, MAIN_ENTREE: 200}` while the sheet showed 200 vegetables
 * and 200 grains — 400 portions dropped with no warning.
 */
export function shouldPersistRateBasis(args: {
  prefillEnabled: boolean
  decomposable: boolean
  typed?: Record<string, number> | null
}): boolean {
  if (!args.decomposable) {
    return false // a sealed or single-kind bundle has nothing to split
  }
  const hasTyped = args.typed != null && Object.keys(args.typed).length > 0
  return args.prefillEnabled || hasTyped
}

/** One column's resolved quantities, for the collapsed summary. */
export interface ComponentSummaryColumn {
  label: string
  mealQty: number
  quantities: Record<string, number>
  /**
   * The column's SYSTEM-MILK total, for menus that ship milk as their own
   * lineup rows instead of as a BEVERAGE part of the bundle. Set it and the
   * preview states milk even though `kinds` carries no BEVERAGE; leave it
   * undefined and milk is simply not a component of this line.
   */
  milkQty?: number
}

/**
 * The collapsed disclosure's self-explaining summary, in QUANTITIES.
 *
 * - everything at one-per-meal → "Shipping every component with each meal"
 * - one shared deviating value  → "135 milk · 120 fruit/juice"
 * - values differ by column     → "135 milk (6-8) · 90 milk (K-5)"
 *
 * Only deviating kinds are named — same rule the percentage version used.
 * Columns with no meals contribute nothing (there is no quantity to state).
 */
export function componentSummaryLabel(kinds: readonly string[], columns: readonly ComponentSummaryColumn[]): string {
  const live = columns.filter((column) => column.mealQty > 0)
  if (live.length === 0) {
    return 'Shipping every component with each meal'
  }
  const chunks: string[] = []
  for (const kind of kinds) {
    if (kind === 'MAIN_ENTREE' || HIDDEN_SPLIT_KINDS.has(kind)) {
      continue
    }
    const deviating = live.filter((column) => (column.quantities[kind] ?? column.mealQty) !== column.mealQty)
    if (deviating.length === 0) {
      continue
    }
    const label = componentKindDisplayLabel(kind).toLowerCase()
    const values = new Set(deviating.map((column) => column.quantities[kind] ?? column.mealQty))
    if (values.size === 1 && deviating.length === live.length) {
      chunks.push(`${[...values][0]} ${label}`)
      continue
    }
    for (const column of deviating) {
      chunks.push(`${column.quantities[kind] ?? column.mealQty} ${label} (${column.label})`)
    }
  }
  return chunks.length === 0 ? 'Shipping every component with each meal' : chunks.join(' · ')
}

/** Short display names for the preview line — terser than the editor labels. */
const PREVIEW_KIND_LABELS: Record<string, string> = {
  MAIN_ENTREE: 'Main',
  FRUIT: 'Fruit',
  VEGETABLE: 'Veg',
  GRAIN: 'Grain',
  BEVERAGE: 'Milk',
  CONDIMENT: 'Condiment',
}

/**
 * One column's previewed quantity for a kind. Everything defaults to the meal
 * count (a component with no number of its own ships one per meal); BEVERAGE
 * falls back to the column's system-milk total when the bundle has no milk
 * part of its own.
 */
function previewQty(column: ComponentSummaryColumn, kind: string): number {
  if (kind === 'BEVERAGE' && column.quantities[kind] === undefined && column.milkQty !== undefined) {
    return column.milkQty
  }
  return column.quantities[kind] ?? column.mealQty
}

/**
 * The collapsed-row preview of a split line, ALWAYS listing every visible
 * component — main and milk included (Sheila, 2026-08-24: the deviation-only
 * summary hid milk and read as "no milk ships"; 2026-08-31: the main was the
 * one component the row never stated, and milk was still missing wherever it
 * ships outside the bundle). Format:
 *
 *   "Main: 150 | Veg: 60 | Fruit: 128 | Condiment: 150 | Milk: 267 (both types)"
 *
 * Per kind, a value shared by every live column renders once; columns that
 * disagree render per column ("Fruit: 92 (K-8) · 60 (9-12)"). The milk entry
 * carries "(both types)" when the school ships more than one milk variety,
 * because its number is the combined total across varieties.
 *
 * MILK OUTSIDE THE BUNDLE: most menus ship milk as their own system rows
 * (`lineupMilkOptionCode`) rather than as a BEVERAGE part, so `kinds` has no
 * BEVERAGE to state — the row's title says "• Milk" while the preview said
 * nothing about it. A column carrying `milkQty` earns the entry; the number
 * is the mealtime's system-milk total for that column, i.e. exactly what the
 * row's own Milk block shows when it is expanded.
 */
export function componentPreviewLabel(
  kinds: readonly string[],
  columns: readonly ComponentSummaryColumn[],
  opts: { milkVarietyCount?: number } = {},
): string {
  const live = columns.filter((column) => column.mealQty > 0)
  if (live.length === 0) {
    return 'Shipping every component with each meal'
  }
  const previewKinds = [...kinds]
  if (!previewKinds.includes('BEVERAGE') && live.some((column) => column.milkQty !== undefined)) {
    previewKinds.push('BEVERAGE')
  }
  const chunks: string[] = []
  // Canonical component order (same ordering bundle titles use) — the
  // first-seen part order read as random (Sheila, 2026-08-24). Main leads and
  // milk trails, matching the row title above the preview.
  const orderedKinds = previewKinds.sort((a, b) => {
    const ia = BUNDLE_TITLE_KIND_ORDER.indexOf(a)
    const ib = BUNDLE_TITLE_KIND_ORDER.indexOf(b)
    return (ia === -1 ? BUNDLE_TITLE_KIND_ORDER.length : ia) - (ib === -1 ? BUNDLE_TITLE_KIND_ORDER.length : ib)
  })
  for (const kind of orderedKinds) {
    if (HIDDEN_SPLIT_KINDS.has(kind)) {
      continue
    }
    const label = PREVIEW_KIND_LABELS[kind] ?? componentKindDisplayLabel(kind)
    const milkSuffix = kind === 'BEVERAGE' && (opts.milkVarietyCount ?? 0) > 1 ? ' (both types)' : ''
    const values = new Set(live.map((column) => previewQty(column, kind)))
    if (values.size === 1) {
      chunks.push(`${label}: ${[...values][0]}${milkSuffix}`)
      continue
    }
    const perColumn = live.map((column) => `${previewQty(column, kind)} (${column.label})`).join(' \u00b7 ')
    chunks.push(`${label}: ${perColumn}${milkSuffix}`)
  }
  return chunks.length === 0 ? 'Shipping every component with each meal' : chunks.join(' | ')
}

/** The wire payload a line's quantities convert to at checkout. */
export interface ComponentWireOverrides {
  /** Per-kind quantities (`componentQuantityOverrides`). */
  componentQuantityOverrides: Record<string, number>
  /**
   * Per-milk-SKU quantities (`componentSkuQuantityOverrides`), keyed by
   * Component id. Null when the line carries no per-SKU numbers or ships
   * no milk (the server falls back to its own share rows).
   */
  componentSkuQuantityOverrides: Record<string, number> | null
}

/**
 * Build the checkout wire payload from a line's quantities.
 *
 * Pass-through, not arithmetic: the numbers the school sees ARE the numbers
 * that ship. The percentage model converted here and could emit a figure the
 * sheet never displayed.
 *
 * Returns `null` for "no opinion" — no typed kinds AND no rate basis — which
 * lets the server apply its own default (`expandShape` when the campus has
 * pre-fill on, else legacy uniform expansion). Note an all-zero map is NOT
 * "no opinion": it is a non-empty map meaning "main only", and it survives.
 */
export function overridesFromQuantities(args: {
  mealQty: number
  typed?: Record<string, number> | null
  rateBps?: Record<string, number> | null
  partCounts?: Record<string, number> | null
  milkSkuQuantities?: Record<string, number> | null
}): ComponentWireOverrides | null {
  const hasTyped = args.typed != null && Object.keys(args.typed).length > 0
  const hasRates = args.rateBps != null && Object.keys(args.rateBps).length > 0
  if (!hasTyped && !hasRates) {
    return null
  }
  const resolved = resolveComponentQuantities(args)
  // The server re-forces this too; being explicit keeps the wire honest.
  resolved['MAIN_ENTREE'] = Math.max(0, args.mealQty)
  const milkQty = resolved['BEVERAGE'] ?? 0
  const skus = args.milkSkuQuantities
  // Per-SKU counts cap at the meal count like every other typed number —
  // the stored map stays raw (see resolveComponentQuantities), so the cap
  // happens here on the way to the wire.
  const componentSkuQuantityOverrides =
    skus && Object.keys(skus).length > 0 && milkQty > 0
      ? Object.fromEntries(
          Object.entries(skus).map(([id, qty]) => [
            id,
            Math.min(Math.max(0, Math.floor(qty)), Math.max(0, args.mealQty)),
          ]),
        )
      : null
  return { componentQuantityOverrides: resolved, componentSkuQuantityOverrides }
}
